<template>
    <div class="scanner-capture">
        <div class="scanner-status">
            <div v-if="!isScanning && !scannedFiles.length" class="scanner-ready">
                <div class="scanner-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 4h18v2H3V4zm0 4h18v8H3V8zm2 2h14v4H5v-4zm4-6h6v2H9V4zm0 4h6v2H9V8z" />
                        <circle cx="7" cy="11" r="1" />
                        <circle cx="11" cy="11" r="1" />
                        <circle cx="15" cy="11" r="1" />
                        <circle cx="7" cy="13" r="1" />
                        <circle cx="11" cy="13" r="1" />
                        <circle cx="15" cy="13" r="1" />
                    </svg>
                </div>
                <h3>اسکنر آماده</h3>
                <p>برای شروع اسکن روی دکمه زیر کلیک کنید</p>
            </div>

            <div v-if="isScanning" class="scanner-scanning">
                <div v-if="!scanPreview" class="scanner-spinner">
                    <div class="spinner"></div>
                </div>
                <div v-if="scanPreview" class="scan-preview">
                    <img :src="scanPreview" alt="Preview" />
                </div>
                <h3>در حال اسکن...</h3>
                <p>لطفاً صبر کنید</p>
            </div>

            <div v-if="error" class="scanner-error">
                <div class="error-icon">⚠️</div>
                <h3>خطا در اسکن</h3>
                <p>{{ error }}</p>
            </div>
        </div>

        <div class="scanner-controls">
            <button 
                @click="startScan" 
                :disabled="isScanning" 
                class="scan-btn"
                :class="{ disabled: isScanning }"
            >
                {{ isScanning ? 'در حال اسکن...' : 'اسکن جدید' }}
            </button>

            <button 
                v-if="scannedFiles.length > 0" 
                @click="clearScannedFiles" 
                class="clear-btn"
            >
                پاک کردن همه
            </button>
        </div>

        <div v-if="scannedFiles.length > 0" class="scan-info">
            <p>تعداد اسناد اسکن شده: {{ scannedFiles.length }}</p>
            <p>برای اسکن سند دیگری، روی دکمه "اسکن جدید" کلیک کنید</p>
        </div>

        <div v-if="scannedFiles.length > 0" class="scanned-files">
            <h4>فایل‌های اسکن شده:</h4>
            <div class="file-list">
                <div v-for="(file, index) in scannedFiles" :key="index" class="file-item">
                    <div class="file-preview">
                        <img v-if="file.type.startsWith('image/')" :src="file.preview" :alt="file.name" />
                        <div v-else class="file-icon">
                            <font-awesome-icon :icon="['fas', 'file']" />
                        </div>
                    </div>
                    <div class="file-info">
                        <span class="file-name">{{ file.name }}</span>
                        <span class="file-size">{{ formatFileSize(file.size) }}</span>
                    </div>
                    <button @click="removeFile(index)" class="remove-btn">×</button>
                </div>
            </div>
        </div>

        <div class="scanner-actions">
            <button 
                v-if="scannedFiles.length > 0" 
                @click="confirmScan" 
                class="confirm-btn"
            >
                تأیید و ذخیره
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';

// eslint-disable-next-line no-undef
const props = defineProps({ 
    patientId: { type: Number, required: true } 
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['confirm', 'close']);

const isScanning = ref(false);
const scannedFiles = ref([]);
const error = ref('');
const scanPreview = ref('');

const startScan = async () => {
    isScanning.value = true;
    error.value = '';
    scanPreview.value = '';

    try {
        // بررسی پشتیبانی از Web API
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('مرورگر شما از Web API پشتیبانی نمی‌کند');
        }

        // تلاش برای دسترسی به اسکنر از طریق Web API
        await initiateScanner();
        
    } catch (err) {
        console.error('خطا در اسکن:', err);
        error.value = err.message || 'خطا در اتصال به اسکنر';
        isScanning.value = false;
        scanPreview.value = '';
    }
};

const initiateScanner = async () => {
    try {
        // بررسی پشتیبانی از Web API
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
            throw new Error('مرورگر شما از Web API پشتیبانی نمی‌کند');
        }

        // دریافت لیست دستگاه‌های ورودی
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        
        if (videoDevices.length === 0) {
            throw new Error('هیچ دوربین یا اسکنری یافت نشد');
        }

        // تلاش برای اتصال به اسکنر واقعی
        await connectToScanner(videoDevices);
        
    } catch (err) {
        console.warn('خطا در اتصال به اسکنر واقعی، استفاده از شبیه‌ساز:', err.message);
        // در صورت عدم موفقیت، از شبیه‌ساز استفاده کن
        await simulateScan();
    }
};

const connectToScanner = async (devices) => {
    try {
        // تلاش برای اتصال به اولین دستگاه ویدیو
        const constraints = {
            video: {
                deviceId: devices[0].deviceId,
                width: { ideal: 1920 },
                height: { ideal: 1080 }
            }
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        
        // ایجاد canvas برای ضبط تصویر
        const video = document.createElement('video');
        video.srcObject = stream;
        video.play();

        return new Promise((resolve, reject) => {
            video.onloadedmetadata = () => {
                // نمایش preview
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = video.videoWidth;
                tempCanvas.height = video.videoHeight;
                const tempCtx = tempCanvas.getContext('2d');
                tempCtx.drawImage(video, 0, 0);
                scanPreview.value = tempCanvas.toDataURL('image/png');
                
                // کمی صبر کن تا کاربر preview را ببیند
                setTimeout(() => {
                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    const ctx = canvas.getContext('2d');
                    
                    // رسم تصویر از ویدیو
                    ctx.drawImage(video, 0, 0);
                    
                    // متوقف کردن stream
                    stream.getTracks().forEach(track => track.stop());
                    
                    // تبدیل به blob
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const file = new File([blob], `scanned_document_${Date.now()}.png`, {
                                type: 'image/png'
                            });
                            
                            // ایجاد preview
                            const reader = new FileReader();
                            reader.onload = (e) => {
                                const scannedFile = {
                                    file: file,
                                    name: file.name,
                                    size: file.size,
                                    type: file.type,
                                    preview: e.target.result
                                };
                                
                                scannedFiles.value.push(scannedFile);
                                isScanning.value = false;
                                scanPreview.value = '';
                                resolve();
                            };
                            reader.readAsDataURL(file);
                        } else {
                            reject(new Error('خطا در ایجاد فایل اسکن شده'));
                        }
                    }, 'image/png');
                }, 1000); // 1 ثانیه نمایش preview
            };
            
            video.onerror = (err) => {
                reject(new Error('خطا در بارگذاری ویدیو: ' + err.message));
            };
        });
        
    } catch (err) {
        throw new Error('خطا در اتصال به اسکنر: ' + err.message);
    }
};

const simulateScan = async () => {
    // شبیه‌سازی فرآیند اسکن
    return new Promise((resolve) => {
        setTimeout(() => {
            // ایجاد فایل شبیه‌سازی شده
            const canvas = document.createElement('canvas');
            canvas.width = 800;
            canvas.height = 600;
            const ctx = canvas.getContext('2d');
            
            // رسم یک مستطیل ساده به عنوان نمونه
            ctx.fillStyle = '#f0f0f0';
            ctx.fillRect(0, 0, 800, 600);
            ctx.fillStyle = '#333';
            ctx.font = '24px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('سند اسکن شده', 400, 300);
            ctx.fillText(new Date().toLocaleString('fa-IR'), 400, 350);
            
            canvas.toBlob((blob) => {
                const file = new File([blob], `scanned_document_${Date.now()}.png`, {
                    type: 'image/png'
                });
                
                // ایجاد preview
                const reader = new FileReader();
                reader.onload = (e) => {
                    const scannedFile = {
                        file: file,
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        preview: e.target.result
                    };
                    
                    scannedFiles.value.push(scannedFile);
                    isScanning.value = false;
                    resolve();
                };
                reader.readAsDataURL(file);
            }, 'image/png');
        }, 2000); // شبیه‌سازی 2 ثانیه اسکن
    });
};

const clearScannedFiles = () => {
    scannedFiles.value = [];
};

const removeFile = (index) => {
    scannedFiles.value.splice(index, 1);
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 بایت';
    const k = 1024;
    const sizes = ['بایت', 'کیلوبایت', 'مگابایت'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const confirmScan = () => {
    if (scannedFiles.value.length > 0) {
        emit('confirm', {
            scannedFiles: scannedFiles.value.map(item => item.file)
        });
    }
};

const closeScanner = () => {
    emit('close');
};
</script>

<style scoped>
.scanner-capture {
    padding: 20px;
    max-width: 600px;
    margin: 0 auto;
}

.scanner-status {
    text-align: center;
    margin-bottom: 30px;
}

.scanner-ready .scanner-icon {
    color: #4CAF50;
    margin-bottom: 15px;
}

.scanner-scanning .scanner-spinner {
    margin-bottom: 15px;
}

.scan-preview {
    margin-bottom: 15px;
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;
}

.scan-preview img {
    width: 100%;
    height: auto;
    border-radius: 8px;
    border: 2px solid #4CAF50;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.scanner-error .error-icon {
    font-size: 48px;
    margin-bottom: 15px;
}

.scanner-controls {
    display: flex;
    gap: 15px;
    justify-content: center;
}

.scan-btn, .clear-btn {
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.3s ease;
}

.scan-btn {
    background-color: var(--background-color-success);
    color: var(--text-color-forthly);
}

.scan-btn:hover:not(.disabled) {
    background-color: var(--background-color-success-hover);
}

.scan-btn.disabled {
    background-color: #cccccc;
    cursor: not-allowed;
}

.clear-btn {
    background-color: #f44336;
    color: white;
}

.clear-btn:hover {
    background-color: #da190b;
}

.scan-info {
    background-color: #e8f5e8;
    border: 1px solid #4CAF50;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 20px;
    text-align: center;
}

.scan-info p {
    margin: 5px 0;
    color: #2e7d32;
    font-weight: 500;
}

.scanned-files {
    margin-bottom: 30px;
}

.file-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.file-item {
    display: flex;
    align-items: center;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background-color: #f9f9f9;
}

.file-preview {
    width: 50px;
    height: 50px;
    margin-left: 10px;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fff;
}

.file-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.file-icon {
    font-size: 24px;
    color: #666;
}

.file-info {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.file-name {
    font-weight: bold;
    margin-bottom: 4px;
}

.file-size {
    font-size: 12px;
    color: #666;
}

.remove-btn {
    background-color: #f44336;
    color: white;
    border: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.remove-btn:hover {
    background-color: #da190b;
}

.scanner-actions {
    display: flex;
    gap: 15px;
    justify-content: center;
}

.confirm-btn {
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.3s ease;
    background-color: #4CAF50;
    color: white;
}

.confirm-btn:hover {
    background-color: #45a049;
}

h3, h4 {
    margin: 10px 0;
    color: #333;
}

p {
    margin: 5px 0;
    color: #666;
}
</style>
